import {
  PipeSeriesPreference,
  pipeSeriesPreferenceSchema,
} from "@src/core/api/pipeSeriesPreference";
import { extractDefaultValues } from "@src/core/engine/helpers/extractDefaultValues";
import { pipesRepository } from "@src/core/engine/pipes/pipesRepository";
import { useToast } from "@src/shared/components/Toast/useToast";
import { useAppForm } from "@src/shared/helpers/form";
import { logger } from "@src/shared/helpers/logger";
import { mergeWithDefaults } from "@src/shared/helpers/mergeWithDefaults";
import { parseApiError } from "@src/shared/helpers/parseApiError";
import { revalidateLogic } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSeriesDefaultPipe } from "../helpers/getSeriesDefaultPipe";
import { updatePipeSeriesPreference } from "../helpers/updatePipeSeriesPreference";
import { usePipeSeriesDataSuspense } from "./usePipeSeriesData";

type UsePipesFormOptions = {
  defaultSeriesId?: string;
  onSubmit?: () => void;
};

export function usePipeSeriesForm(options?: UsePipesFormOptions) {
  const { addToast } = useToast();
  const { t } = useTranslation("settings", { keyPrefix: "pipeSeries" });
  const [submitting, setSubmitting] = useState(false);

  const { customPipeSeries, pipeSeriesPreference } =
    usePipeSeriesDataSuspense();
  const defaultSeriesId =
    options?.defaultSeriesId ?? pipeSeriesPreference?.pipeSeries;
  const initialValues = {
    pipes:
      getSeriesDefaultPipe(options?.defaultSeriesId, customPipeSeries) ??
      pipeSeriesPreference?.pipes,
    pipeSeries: defaultSeriesId,
  };
  const defaultValue: PipeSeriesPreference = useMemo(
    () =>
      extractDefaultValues(
        pipesRepository.getDefinitions(initialValues, customPipeSeries),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const form = useAppForm({
    defaultValues: mergeWithDefaults(initialValues, defaultValue),
    async onSubmit({ value }) {
      setSubmitting(true);
      const [error] = await updatePipeSeriesPreference(value, customPipeSeries);
      setSubmitting(false);

      if (error) {
        const apiError = parseApiError({
          error,
          pathAliases: { pipeId: "pipes", pipeSeriesId: "pipeSeries" },
        });
        logger.error("Failed to update pipe series preference", {
          apiError,
          error,
        });
        return;
      }

      addToast({
        duration: 3000,
        message: t("update.success.description"),
        title: t("update.success.title"),
      });
      options?.onSubmit?.();
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: pipeSeriesPreferenceSchema },
  });

  return {
    defaultValue,
    definitions: pipesRepository.getDefinitions(undefined, customPipeSeries),
    form,
    submitting,
  };
}
